import { useEffect } from 'react';
import { useAppDispatch } from 'app/hooks';
import {
  projectBoardApi,
  useGetCostingsQuery,
  usePatchCostingMutation,
} from 'app/services/projectBoardApi';
import { getSocket, type SocketDataMessage } from 'app/socketClient';
import type { ICostingCeleryData, ICostingRow } from 'models';
import { useTabAccess } from '../../hooks/useProjectBoardData';
import { useCeleryReport } from '../../hooks/useCeleryReport';
import { generationTimeStamp } from '../../projectBoardUtils';
import FormattedNumberInput from './FormattedNumberInput';
import { GridRow, SingleUnitCol, MoneyCol, PlainCol, PctAmtCol } from './gridCells';

/**
 * COSTING TAB (`#costing` — `costing/view/costing.js` + costing.tmpl),
 * inventory §B2 / parity 156–163.
 *
 * Boot order (faithful): `GET /api/v1.0/projects/costings/` FIRST (unfiltered
 * list; the row is found client-side by project_id), then the dotted
 * preloader + celery `POST /reports/costing/project` round trip; render only
 * after both. RECALC re-shows the preloader and re-runs ONLY the celery leg
 * (no collection refetch). dev3 has no websocket ⇒ the preloader is the
 * faithful eternal state (risk b).
 *
 * Edits (parity 158): editable ACTUAL %/US$ pairs clamp to data-min/max via
 * the formatter (clamped value written back into the input), then
 * `PATCH /projects/costings/{project_id}` with the PINNED body
 * `{generate_id, change_id?, field}` (see patchCosting in projectBoardApi —
 * change_id is omitted-when-absent exactly like the Backbone undefined attr).
 * The echo's `set_values` recomputed pairs take display precedence
 * (`{{#ifCompare change* '!=' null}}`), and the echoed top-level
 * cost_net_actual / price_raw_actual / price_list_actual make the read-only
 * ACTUAL cells prefer the stored row over the celery figures (parity 159).
 *
 * Realtime (parity 162): socket put on django model `ksdmprojectaccounting`
 * (models.mapping → 'model.project.costing', row keyed by project_id) merges
 * into the cached row.
 *
 * [NEG] honored (parity 163): the costing `keydown_backspace` window.name bug
 * (NO keydown handler at all here); Engineering/Plant commented rows; the
 * unused passed-in costing model + company/contact collections; the local
 * costing/timeline collection twin.
 *
 * DELIBERATE NON-REPLICATION (3c precedent): when a committed value equals
 * the model attr, the legacy `unset(name)` fires an EXTRA no-field
 * `{generate_id}` PATCH before the real one (pure write amplification — the
 * server recomputes and echoes the same set_values). We send the single real
 * PATCH the unset+set sequence was forcing.
 */

const COSTING_DJANGO_MODEL = 'ksdmprojectaccounting';

/** RO costing cell class (legacy `non-clickable output-field number-field`). */
const RO = 'non-clickable output-field number-field';

const CostingTab = ({ projectId }: { projectId: number }) => {
  const dispatch = useAppDispatch();
  const { data: costings } = useGetCostingsQuery();
  const access = useTabAccess(projectId, 'costing');
  const [patchCosting] = usePatchCostingMutation();

  // Boot order gate: celery fires only once the collection fetch resolved.
  const celery = useCeleryReport<ICostingCeleryData>(
    '/reports/costing/project',
    projectId,
    !!costings,
    'costing_data'
  );

  // Realtime put: ksdmprojectaccounting → merge into the cached costing row
  // (syncdata listener parity — msg.data.id is the registry key = project_id).
  useEffect(() => {
    const socket = getSocket();
    const onData = (msg: SocketDataMessage) => {
      if (!msg || msg.model !== COSTING_DJANGO_MODEL || msg.code !== 'put') return;
      const data = (msg.data ?? {}) as Record<string, unknown>;
      const id = Number((data as { id?: unknown }).id ?? msg.id);
      if (!Number.isFinite(id)) return;
      dispatch(
        projectBoardApi.util.updateQueryData('getCostings', undefined, (draft) => {
          const row = draft.find((r) => r.project_id === id);
          if (row) Object.assign(row, data);
        })
      );
    };
    socket.on('data', onData);
    return () => {
      socket.off('data', onData);
    };
  }, [dispatch]);

  // Legacy: #costing stays EMPTY until the collection fetch resolves.
  if (!costings) return null;

  const row: ICostingRow | undefined = costings.find((r) => r.project_id === projectId);
  const accounting = celery.data;

  // {{#unless accounting}} → dotted preloader (boot, RECALC, and the
  // missing-row crash path which legacy leaves on the preloader too).
  if (celery.pending || !accounting || !row) {
    return (
      <div className="content col-xs-12">
        <div className="content-item row">
          <div className="dotted-preloader active" />
        </div>
      </div>
    );
  }

  const locked = !access; // parity 161: inputs disabled + buttons non-clickable

  /** set_values display precedence ({{#ifCompare change* '!=' null}}). */
  const setValue = (field: string): unknown => {
    const v = row.set_values?.[field];
    return v !== null && v !== undefined ? v : undefined;
  };
  const actual = (field: keyof ICostingRow & string): number | string | null | undefined =>
    (setValue(field) as number | string | null | undefined) ??
    (row[field] as number | string | null | undefined);

  /** Read-only ACTUAL: stored-over-celery via Handlebars truthiness. */
  const storedOrCelery = (
    stored: number | string | null | undefined,
    fromCelery: number | string | null | undefined
  ): number | string | null | undefined => (stored ? stored : fromCelery);

  // channge_input (sic): `getValue() || 0` → clamp (done by the formatter
  // input) → set + PATCH {generate_id, change_id?, field}.
  const commit = (name: string, v: number | null) => {
    const value = v || 0;
    const body: Record<string, unknown> = {
      generate_id: celery.generateId ?? undefined,
      [name]: value,
    };
    if (row.change_id !== undefined) body.change_id = row.change_id;
    patchCosting({
      projectId,
      set: { [name]: value, generate_id: celery.generateId ?? undefined },
      body,
    });
  };

  const onRecalc = () => {
    if (locked) return;
    celery.recalc();
  };

  // Editable %/US$ pair (admin fee / inflation / general contingency /
  // commission / net profit) — exact attrs per costing.tmpl.
  const pct = (
    name: string,
    opts: {
      className?: string;
      min?: string;
      max: string;
      bound?: string;
    }
  ) => (
    <FormattedNumberInput
      name={name}
      value={actual(name)}
      mode="decimal"
      className={opts.className ?? 'percent change_field number-field'}
      min={opts.min}
      max={opts.max}
      boundParameter={opts.bound}
      disabled={locked}
      onCommit={commit}
    />
  );
  const amt = (
    name: string,
    opts: { bound: string; min?: string; step?: string }
  ) => (
    <FormattedNumberInput
      name={name}
      value={actual(name)}
      mode="integer"
      className="price change_field number-field"
      min={opts.min}
      step={opts.step}
      max="10000000"
      boundParameter={opts.bound}
      disabled={locked}
      onCommit={commit}
    />
  );
  const roPct = (value: number | string | null | undefined) => (
    <FormattedNumberInput
      value={value}
      mode="decimal"
      className={RO}
      output
      tabIndex={-1}
      disabled={locked}
    />
  );
  const roInt = (value: number | string | null | undefined, className = RO) => (
    <FormattedNumberInput
      value={value}
      mode="integer"
      className={className}
      output
      tabIndex={-1}
      disabled={locked}
    />
  );

  return (
    <div className="content col-xs-12">
      <div className="time-stamp row">Generated on : {generationTimeStamp()}</div>
      {/* dev-sync G / UF-1 — `costing-content-item` adds the 55px bottom
          padding the absolutely-positioned footer note below sits in
          (costing.tmpl:9; project/styles.css `.costing-content-item`).
          COSTING ONLY: dev deliberately did not add either to the Pricing
          tab, even though the CSS covers `#pricing` too. */}
      <div className="content-item row costing-content-item">
        <div className="col-lg-8 col-md-9 col-xs-12">
          <div className="row">
            {/* Header: DESCRIPTION | SCHEDULED | ACTUAL */}
            <div className="col-xs-12 input-label-group-heading">
              <div className="row">
                <div className="col-xs-12">
                  <div className="row">
                    <div className="list-inline col-xs-12">
                      <div className="col-xs-3">
                        <div className="row">
                          <h3 className="left">DESCRIPTION</h3>
                        </div>
                      </div>
                      <div className="col-xs-9">
                        <div className="row">
                          <div className="col-xs-6">
                            <div className="row">
                              <h3>SCHEDULED</h3>
                            </div>
                          </div>
                          <div className="col-xs-6">
                            <div className="row">
                              <h3>ACTUAL</h3>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="input-label-group sched-act-uom col-xs-12">
              <div className="row ">
                {/* SCHEDULED-only read-only rows (celery figures) */}
                <GridRow label="Material" className="costing-item top-margin">
                  <SingleUnitCol unit="US$">
                    <FormattedNumberInput
                      name="mtl_cost"
                      value={accounting.mtl_cost}
                      mode="integer"
                      className={`mtl_cost ${RO}`}
                      min="0"
                      step="1"
                      max="10000000"
                      output
                      tabIndex={-1}
                      disabled={locked}
                    />
                  </SingleUnitCol>
                </GridRow>
                <GridRow label="Material IB Freight">
                  <SingleUnitCol unit="US$">
                    <FormattedNumberInput
                      name="freight_cost_sched"
                      value={accounting.freight_cost_sched}
                      mode="integer"
                      className={`freight_cost_sched ${RO}`}
                      min="0"
                      step="1"
                      max="10000000"
                      output
                      tabIndex={-1}
                      disabled={locked}
                    />
                  </SingleUnitCol>
                </GridRow>
                <GridRow label="Activities">
                  <SingleUnitCol unit="US$">
                    <FormattedNumberInput
                      name="activities_cost_sched"
                      value={accounting.activities_cost_sched}
                      mode="integer"
                      className={`activities_cost_sched ${RO}`}
                      min="0"
                      step="1"
                      max="10000000"
                      output
                      tabIndex={-1}
                      disabled={locked}
                    />
                  </SingleUnitCol>
                </GridRow>
                <GridRow label="Activities">
                  <SingleUnitCol unit="hr">
                    <FormattedNumberInput
                      name="activities_hrs_sched"
                      value={accounting.activities_hrs_sched}
                      mode="integer"
                      className={`activities_hrs_sched ${RO}`}
                      min="0"
                      step="1"
                      max="10000000"
                      output
                      tabIndex={-1}
                      disabled={locked}
                    />
                  </SingleUnitCol>
                </GridRow>
                {/* [NEG] Engineering / Plant rows — fully commented in the tmpl. */}

                {/* Editable ACTUAL %/US$ pairs */}
                <GridRow label="Administration Fee">
                  <PctAmtCol
                    pct={roPct(accounting.admin_fee_sched_percent)}
                    amt={roInt(accounting.admin_fee_sched_amount)}
                  />
                  <PctAmtCol
                    pct={pct('admin_fee_actual_percent', {
                      // Literal `if`/`else` classes from the override branches.
                      className:
                        setValue('admin_fee_actual_percent') !== undefined
                          ? 'percent if change_field number-field'
                          : 'percent else change_field number-field',
                      min: '0.0',
                      max:
                        setValue('admin_fee_actual_percent') !== undefined
                          ? '100.00'
                          : '100.0',
                    })}
                    amt={amt('admin_fee_actual_amount', {
                      bound: 'admin_fee_actual_percent',
                    })}
                  />
                </GridRow>
                <GridRow label="Inflation Contingency">
                  <PctAmtCol
                    pct={roPct(accounting.inflation_contingency_sched_percent)}
                    amt={roInt(accounting.inflation_contingency_sched_amount)}
                  />
                  <PctAmtCol
                    pct={pct('inflation_contingency_actual_percent', {
                      min: '0',
                      max: '100',
                      bound: 'inflation_contingency_actual_amount',
                    })}
                    amt={amt('inflation_contingency_actual_amount', {
                      bound: 'inflation_contingency_actual_percent',
                    })}
                  />
                </GridRow>
                <GridRow label="General Contingency">
                  <PctAmtCol
                    pct={roPct(accounting.gen_contingency_sched_percent)}
                    amt={roInt(accounting.gen_contingency_sched_amount)}
                  />
                  <PctAmtCol
                    pct={pct('gen_contingency_actual_percent', {
                      min: '0',
                      max: '100',
                      bound: 'gen_contingency_actual_amount',
                    })}
                    amt={amt('gen_contingency_actual_amount', {
                      bound: 'gen_contingency_actual_percent',
                      min: '0',
                      step: '1',
                    })}
                  />
                </GridRow>
                <GridRow label="Project Net Cost" className="costing-item bold">
                  <MoneyCol>{roInt(accounting.cost_net_sched)}</MoneyCol>
                  <MoneyCol>
                    {roInt(
                      storedOrCelery(row.cost_net_actual, accounting.cost_net_actual),
                      `cost_net_actual ${RO}`
                    )}
                  </MoneyCol>
                </GridRow>
                <GridRow
                  label="Mfg's Internal Sales Commission of Gross Price"
                  className="costing-item top-margin"
                >
                  <PctAmtCol
                    pct={roPct(row.int_sales_commission_sched_percent)}
                    amt={roInt(accounting.int_sales_commission_sched_amount)}
                  />
                  <PctAmtCol
                    pct={pct('int_sales_commission_actual_percent', {
                      max: '99',
                      bound: 'int_sales_commission_actual_amount',
                    })}
                    // data-boundparameter self-reference — tmpl quirk, replicated.
                    amt={amt('int_sales_commission_actual_amount', {
                      bound: 'int_sales_commission_actual_amount',
                    })}
                  />
                </GridRow>
                <GridRow label="Mfg's Net Profit of Gross Price">
                  <PctAmtCol
                    pct={roPct(row.net_profit_sched_percent)}
                    amt={roInt(accounting.net_profit_sched_amount)}
                  />
                  <PctAmtCol
                    pct={pct('net_profit_actual_percent', {
                      max: '99',
                      bound: 'net_profit_actual_amount',
                    })}
                    amt={amt('net_profit_actual_amount', {
                      bound: 'net_profit_actual_percent',
                    })}
                  />
                </GridRow>
                <GridRow label="Project Raw Price">
                  <MoneyCol>{roInt(accounting.price_raw_sched)}</MoneyCol>
                  <MoneyCol>
                    {roInt(
                      storedOrCelery(row.price_raw_actual, accounting.price_raw_actual)
                    )}
                  </MoneyCol>
                </GridRow>
                <GridRow label="List Price Factor">
                  <PlainCol>
                    <FormattedNumberInput
                      value={row.list_price_factor}
                      mode="float"
                      className={RO}
                      output
                      tabIndex={-1}
                      disabled={locked}
                    />
                  </PlainCol>
                  <PlainCol>
                    <FormattedNumberInput
                      value={row.list_price_factor}
                      mode="float"
                      className={RO}
                      output
                      tabIndex={-1}
                      disabled={locked}
                    />
                  </PlainCol>
                </GridRow>
                <GridRow label="Project List Price" className="costing-item bold">
                  <MoneyCol>{roInt(accounting.price_list_sched)}</MoneyCol>
                  <MoneyCol>
                    {roInt(
                      storedOrCelery(row.price_list_actual, accounting.price_list_actual)
                    )}
                  </MoneyCol>
                </GridRow>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-3 col-xs-12 reload_button">
          <button
            id="refresh_costing"
            className={`btn-add right${locked ? ' non-clickable' : ''}`}
            onClick={onRecalc}
          >
            RECALC
          </button>
        </div>
        {/* dev-sync G / UF-1 (costing.tmpl:1112-1114) — sibling of the RECALC
            .reload_button, pinned bottom-left of the card by
            `.footer-note-wrapper { position:absolute }`. The exact class names
            matter: the CSS already shipped in the merged project/styles.css. */}
        <div className="col-xs-12 footer-note-wrapper">
          <span className="footer-note">
            Project Cost includes all &quot;Main&quot; Air Handling Units and no versions.
          </span>
        </div>
      </div>
    </div>
  );
};

export default CostingTab;
